import { AuthContext, useIsHydrated } from '@core/shared';
import { MarketingCampaignStatus, OptionsApi } from '@core/swagger-client';
import { Button, ContainerDefault, ErrorMessage, ErrorMessagePage, Heading, LoaderPage } from '@core/ui';
import { useNavigate, useParams } from '@online-webinar/router';
import { BackToOriginLink, Link } from '@online-webinar/ui-frontend';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useContext } from 'react';
import { getI18n, useTranslation } from 'react-i18next';

const formatDate = (value?: string) =>
    value ? DateTime.fromISO(value).toLocal().setLocale(getI18n().language).toLocaleString(DateTime.DATETIME_SHORT) : '-';

export const WebinarCampaignsPage = () => {
    const canManageEmailLists = useContext(AuthContext).canManageEmailLists;
    const { t } = useTranslation();
    const nav = useNavigate();
    const isHydrated = useIsHydrated();

    const { webinarId } = useParams({ strict: false });
    const wid = webinarId ?? '';

    const { data: campaigns, isLoading, error } = useQuery(OptionsApi.marketingCampaigns.get(wid, { enabled: !!wid, refetchInterval: 15000 }));
    const createMutation = useMutation(OptionsApi.marketingCampaigns.create());

    if (!canManageEmailLists) {
        return <ErrorMessagePage statusCode={404} />;
    }
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }
    if (isLoading || !campaigns) {
        return <LoaderPage />;
    }

    const createCampaign = () => {
        createMutation.mutate(
            { webinarId: wid, name: t('Campaign.NewName'), subject: '', body: '', excludeInterested: true },
            { onSuccess: (id) => nav({ to: '/$webinarId/marketing/$id', params: { webinarId: wid, id } }) }
        );
    };

    return (
        <ContainerDefault>
            <BackToOriginLink fallback="content" />
            <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                {t('Marketing.PageHeading')}
            </Heading>

            {createMutation.error && <ErrorMessage apiError={createMutation.error} classNames="mb-4 max-w-4xl" />}

            <Button data-testid="campaign-new" variant="primary" disabled={!isHydrated || createMutation.isPending} onClick={createCampaign}>
                {t('Campaign.New')}
            </Button>

            {campaigns.length > 0 && (
                <div className="table-responsive bg-white rounded-lg mt-8 shadow-md">
                    <table className="table table-striped mt-2" data-testid="campaigns-table">
                        <thead>
                            <tr>
                                <th>{t('Campaign.Table.Name')}</th>
                                <th>{t('Campaign.Table.Created')}</th>
                                <th>{t('Campaign.Table.Status')}</th>
                                <th>{t('Campaign.Table.Sent')}</th>
                                <th>{t('Campaign.Table.InterestedNow')}</th>
                                <th className="w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {campaigns.map((campaign) => (
                                <tr key={campaign.id}>
                                    <td>{campaign.name}</td>
                                    <td>{formatDate(campaign.createdDate)}</td>
                                    <td>{t(`Campaign.Status.${MarketingCampaignStatus[campaign.status]}`)}</td>
                                    <td>{campaign.sentCount}</td>
                                    <td>{campaign.interestedNowCount}</td>
                                    <td>
                                        <Link
                                            data-testid={`campaign-detail-link-${campaign.id}`}
                                            to="/$webinarId/marketing/$id"
                                            params={{ webinarId: wid, id: campaign.id }}
                                            variant="link"
                                        >
                                            {t('Generic.Button.Details')}
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </ContainerDefault>
    );
};
